import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DocumentDuplicateIcon, 
  ShareIcon,
  GlobeAltIcon,
  PlayIcon,
  CameraIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/use-toast';

interface SocialShareProps {
  title: string;
  url: string;
  excerpt?: string;
  variant?: 'post' | 'follow';
}

const SocialShare = ({ title, url, excerpt, variant = 'post' }: SocialShareProps) => {
  const [isSharing, setIsSharing] = useState(false);
  const { toast } = useToast();

  const shareData = {
    title,
    text: excerpt || title,
    url,
  };

  const socialLinks = {
    facebook: variant === 'follow' 
      ? 'https://www.facebook.com/seniorthrive'
      : `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&t=${encodeURIComponent(title)}`,
    twitter: variant === 'follow'
      ? 'https://twitter.com/seniorthrive'
      : `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    linkedin: variant === 'follow'
      ? 'https://www.linkedin.com/company/seniorthrive'
      : `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    youtube: 'https://www.youtube.com/@SeniorThrive',
    instagram: 'https://www.instagram.com/seniorthrivelife/',
    tiktok: 'https://www.tiktok.com/@seniorthrive'
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopyLink();
      return;
    }

    setIsSharing(true);
    try {
      await navigator.share(shareData);
      toast({
        title: 'Shared successfully!',
        description: 'Thank you for sharing this article.',
      });
    } catch (error) {
      // User cancelled sharing
      console.log('Share cancelled');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'Link copied!',
        description: 'The link has been copied to your clipboard.',
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Could not copy link to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const openSocialLink = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'follow') {
    return (
      <div className="flex flex-wrap gap-3">
        <h3 className="w-full text-lg font-semibold text-foreground mb-2">Follow SeniorThrive</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.facebook)}
          className="flex items-center gap-2"
        >
          <GlobeAltIcon className="w-4 h-4" />
          Facebook
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.youtube)}
          className="flex items-center gap-2"
        >
          <PlayIcon className="w-4 h-4" />
          YouTube
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.instagram)}
          className="flex items-center gap-2"
        >
          <CameraIcon className="w-4 h-4" />
          Instagram
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.linkedin)}
          className="flex items-center gap-2"
        >
          <UserGroupIcon className="w-4 h-4" />
          LinkedIn
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t border-b py-6 my-8">
      <h3 className="text-lg font-semibold text-foreground mb-4">Share this article</h3>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.facebook)}
          className="flex items-center gap-2"
        >
          <GlobeAltIcon className="w-4 h-4" />
          Facebook
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.twitter)}
          className="flex items-center gap-2"
        >
          <GlobeAltIcon className="w-4 h-4" />
          Twitter
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openSocialLink(socialLinks.linkedin)}
          className="flex items-center gap-2"
        >
          <UserGroupIcon className="w-4 h-4" />
          LinkedIn
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className="flex items-center gap-2"
        >
          <DocumentDuplicateIcon className="w-4 h-4" />
          Copy Link
        </Button>
        {navigator.share && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleNativeShare}
            disabled={isSharing}
            className="flex items-center gap-2"
          >
            <ShareIcon className="w-4 h-4" />
            Share
          </Button>
        )}
      </div>
    </div>
  );
};

export default SocialShare;